/** The draft an admin edits: "All projects" or a checklist of ids. */
export type AssignmentDraft = { specific: boolean; picked: string[] };

/** Start a draft from the saved assignment. Any saved id, even one for a deleted project, puts the
 *  draft in "Only these projects", so a stale-only list shows up as needing a fix, not as "All". */
export function initialDraft(saved: string[]): AssignmentDraft {
  return { specific: saved.length > 0, picked: saved };
}

/** What Save would send for a draft, and whether it differs from the saved assignment.
 *  `known` is the ids the checklist can show. Stale ids are dropped from the payload, so a
 *  subset never carries a deleted project, and a subset with nothing visibly checked is invalid
 *  (sending it would be `[]`, which means all projects). Dirty compares against the raw saved list,
 *  so a stale-only assignment can still be cleared. */
export function planAssignment(draft: AssignmentDraft, saved: string[], known: readonly string[]) {
  const picked = unique(draft.picked).filter((id) => known.includes(id));
  const projectIds = draft.specific ? picked : [];
  const invalid = draft.specific && picked.length === 0;
  const dirty = !sameIds(projectIds, saved);
  return { projectIds, invalid, dirty, canSave: dirty && !invalid };
}

/** Set equality over ids: order and duplicates do not matter. */
export function sameIds(a: string[], b: string[]): boolean {
  const left = new Set(a);
  const right = new Set(b);
  return left.size === right.size && [...left].every((id) => right.has(id));
}

function unique(ids: string[]): string[] {
  return [...new Set(ids)];
}
